// src/services/orphanContentService.ts

import apiClient from '../api/apiClient';
import {
  OrphanContent,
  ClaimRequest,
  EvidenceItem
} from '@/types/marketplace';
import { StrapiResponse } from '@/types/common';

class OrphanContentService {
  // Orphan content management
  async getOrphanContents(page: number = 1, limit: number = 20, filters?: any): Promise<{ data: OrphanContent[]; meta: any }> {
    const queryParams: Record<string, any> = {
      'filters[status][$eq]': 'available',
      'populate': 'deep',
      'pagination[page]': page.toString(),
      'pagination[pageSize]': limit.toString(),
      'sort': this.buildSortParams(filters?.sortBy)
    };

    // Add filters
    if (filters) {
      this.addFiltersToQueryParams(queryParams, filters);
    }

    const response = await apiClient.get<StrapiResponse<OrphanContent[]>>('/api/orphan-contents', {
      params: queryParams
    });
    
    return {
      data: response.data.data || [],
      meta: response.data.meta || {}
    };
  }

  async getFeaturedOrphanContents(limit: number = 10): Promise<OrphanContent[]> {
    const queryParams = {
      'filters[status][$eq]': 'available',
      'populate': 'deep',
      'sort': 'potentialRevenue:desc',
      'pagination[pageSize]': limit.toString()
    };

    const response = await apiClient.get<StrapiResponse<OrphanContent[]>>('/api/orphan-contents', {
      params: queryParams
    });
    
    return response.data.data || [];
  }

  async getOrphanContentById(contentId: string): Promise<OrphanContent> {
    const queryParams = {
      'populate': 'deep'
    };

    const response = await apiClient.get<StrapiResponse<OrphanContent>>(`/api/orphan-contents/${contentId}`, {
      params: queryParams
    });
    
    return response.data.data;
  }

  async searchOrphanContents(query: string, filters?: any): Promise<OrphanContent[]> {
    const queryParams: Record<string, any> = {
      'filters[status][$eq]': 'available',
      'populate': 'deep'
    };

    // Add search query
    if (query?.trim()) {
      queryParams['filters[$or][0][title][$containsi]'] = query;
      queryParams['filters[$or][1][description][$containsi]'] = query;
    }

    // Add filters
    if (filters) {
      this.addFiltersToQueryParams(queryParams, filters);
    }

    const response = await apiClient.get<StrapiResponse<OrphanContent[]>>('/api/orphan-contents', {
      params: queryParams
    });
    
    return response.data.data || [];
  }

  // Claim request management
  async submitClaimRequest(orphanContentId: string, claimData: {
    claimantInfo: {
      name: string;
      email: string;
      phone?: string;
      relationship: 'author' | 'heir' | 'publisher' | 'representative';
    };
    evidence: EvidenceItem[];
    statement: string;
  }): Promise<ClaimRequest> {
    const response = await apiClient.post<StrapiResponse<ClaimRequest>>('/api/claim-requests', {
      data: {
        orphanContent: orphanContentId,
        claimantInfo: claimData.claimantInfo,
        evidence: claimData.evidence,
        statement: claimData.statement,
        status: 'submitted',
        submittedAt: new Date().toISOString()
      }
    });

    return response.data.data;
  }

  async updateClaimRequest(requestId: string, updateData: {
    evidence?: EvidenceItem[];
    statement?: string;
  }): Promise<ClaimRequest> {
    const response = await apiClient.put<StrapiResponse<ClaimRequest>>(`/api/claim-requests/${requestId}`, {
      data: {
        ...updateData,
        
      }
    });

    return response.data.data;
  }

  async withdrawClaimRequest(requestId: string): Promise<void> {
    await apiClient.delete(`/api/claim-requests/${requestId}`);
  }

  async getUserClaimRequests(userId: string): Promise<ClaimRequest[]> {
    const queryParams = {
      'filters[claimant][id][$eq]': userId,
      'populate': 'deep',
      'sort': 'submittedAt:desc'
    };

    const response = await apiClient.get<StrapiResponse<ClaimRequest[]>>('/api/claim-requests', {
      params: queryParams
    });
    
    return response.data.data || [];
  }

  async getAllClaimRequests(): Promise<ClaimRequest[]> {
    const queryParams = {
      'populate': 'deep',
      'sort': 'submittedAt:desc'
    };

    const response = await apiClient.get<StrapiResponse<ClaimRequest[]>>('/api/claim-requests', {
      params: queryParams
    });
    
    return response.data.data || [];
  }

  async getClaimRequestById(requestId: string): Promise<ClaimRequest> {
    const queryParams = {
      'populate': 'deep'
    };

    const response = await apiClient.get<StrapiResponse<ClaimRequest>>(`/api/claim-requests/${requestId}`, {
      params: queryParams
    });
    
    return response.data.data;
  }

  // Admin/Staff actions
  async reviewClaimRequest(requestId: string, decision: 'approved' | 'rejected', reviewNotes?: string): Promise<ClaimRequest> {
    const response = await apiClient.put<StrapiResponse<ClaimRequest>>(`/api/claim-requests/${requestId}`, {
      data: {
        status: decision === 'approved' ? 'approved' : 'rejected',
        reviewNotes,
        reviewedAt: new Date().toISOString()
      }
    });

    // If approved, update the orphan content status
    if (decision === 'approved') {
      const claimRequest = response.data.data;
      const orphanContentId = claimRequest.attributes?.orphanContent?.documentId || 
                             claimRequest.orphanContent?.documentId || 
                             claimRequest.orphanContent;
      
      if (orphanContentId) {
        await this.updateOrphanContentStatus(orphanContentId, 'claimed');
      }
    }

    return response.data.data;
  }

  async assignClaimRequestToReviewer(requestId: string, reviewerId: string): Promise<ClaimRequest> {
    const response = await apiClient.put<StrapiResponse<ClaimRequest>>(`/api/claim-requests/${requestId}`, {
      data: {
        reviewedBy: reviewerId,
        status: 'under_review',
        assignedAt: new Date().toISOString()
      }
    });

    return response.data.data;
  }

  async requestAdditionalEvidence(requestId: string, requiredEvidence: string[]): Promise<ClaimRequest> {
    const response = await apiClient.put<StrapiResponse<ClaimRequest>>(`/api/claim-requests/${requestId}`, {
      data: {
        status: 'additional_evidence_required',
        requiredEvidence,
        evidenceRequestedAt: new Date().toISOString()
      }
    });

    return response.data.data;
  }

  private async updateOrphanContentStatus(contentId: string, status: 'available' | 'claimed' | 'disputed'): Promise<void> {
    try {
      await apiClient.put(`/api/orphan-contents/${contentId}`, {
        data: {
          status
        }
      });
    } catch (error) {
      console.warn('Failed to update orphan content status:', error);
    }
  }

  // Evidence management
  async uploadEvidence(claimRequestId: string, evidenceData: {
    type: 'document' | 'certificate' | 'publication' | 'witness' | 'other';
    title: string;
    description: string;
    file: File;
  }): Promise<EvidenceItem> {
    try {
      // First, upload the file
      const formData = new FormData();
      formData.append('files', evidenceData.file);

      const uploadResponse = await apiClient.upload('POST', '/api/upload', formData);
      const fileUrl = uploadResponse.data[0]?.url;

      if (!fileUrl) {
        throw new Error('Failed to upload file');
      }

      // Create evidence item
      const evidenceItem: EvidenceItem = {
        type: evidenceData.type,
        title: evidenceData.title,
        description: evidenceData.description,
        fileUrl,
        uploadedAt: new Date().toISOString()
      };

      // Update claim request with new evidence
      const claimRequest = await this.getClaimRequestById(claimRequestId);
      const currentEvidence = claimRequest.attributes?.evidence || claimRequest.evidence || [];
      const updatedEvidence = [...currentEvidence, evidenceItem];

      await apiClient.put(`/api/claim-requests/${claimRequestId}`, {
        data: {
          evidence: updatedEvidence
        }
      });

      return evidenceItem;
    } catch (error) {
      console.error('Failed to upload evidence:', error);
      throw error;
    }
  }

  async deleteEvidence(claimRequestId: string, evidenceFileUrl: string): Promise<void> {
    try {
      // Get current claim request
      const claimRequest = await this.getClaimRequestById(claimRequestId);
      const currentEvidence = claimRequest.attributes?.evidence || claimRequest.evidence || [];
      
      // Remove evidence from list
      const updatedEvidence = currentEvidence.filter(
        (e: EvidenceItem) => e.fileUrl !== evidenceFileUrl
      );

      // Update claim request
      await apiClient.put(`/api/claim-requests/${claimRequestId}`, {
        data: {
          evidence: updatedEvidence
        }
      });
    } catch (error) {
      console.error('Failed to delete evidence:', error);
      throw error;
    }
  }

  async validateEvidence(claimRequestId: string, evidenceFileUrl: string, isValid: boolean, validationNotes?: string): Promise<void> {
    try {
      const claimRequest = await this.getClaimRequestById(claimRequestId);
      const currentEvidence = claimRequest.attributes?.evidence || claimRequest.evidence || [];
      
      const updatedEvidence = currentEvidence.map((evidence: EvidenceItem) => {
        if (evidence.fileUrl === evidenceFileUrl) {
          return {
            ...evidence,
            isValidated: true,
            isValid,
            validationNotes,
            validatedAt: new Date().toISOString()
          };
        }
        return evidence;
      });

      await apiClient.put(`/api/claim-requests/${claimRequestId}`, {
        data: {
          evidence: updatedEvidence
        }
      });
    } catch (error) {
      console.error('Failed to validate evidence:', error);
      throw error;
    }
  }

  // Statistics and analytics
  async getOrphanContentStats(): Promise<any> {
    try {
      const response = await apiClient.get('/api/orphan-contents/statistics');
      return response.data;
    } catch (error) {
      console.warn('Failed to get orphan content stats:', error);
      return {};
    }
  }

  async getClaimRequestStats(): Promise<any> {
    try {
      const response = await apiClient.get('/api/claim-requests/statistics');
      return response.data;
    } catch (error) {
      console.warn('Failed to get claim request stats:', error);
      return {};
    }
  }

  async getOrphanContentAnalytics(period?: { from: string; to: string }): Promise<any> {
    const queryParams: Record<string, string> = {};

    if (period) {
      queryParams.from = period.from;
      queryParams.to = period.to;
    }

    try {
      const response = await apiClient.get('/api/orphan-contents/analytics', {
        params: queryParams
      });
      
      return response.data;
    } catch (error) {
      console.warn('Failed to get orphan content analytics:', error);
      return {};
    }
  }

  // Bulk operations
  async bulkUpdateOrphanContentStatus(contentIds: string[], status: 'available' | 'claimed' | 'disputed'): Promise<void> {
    try {
      await apiClient.post('/api/orphan-contents/bulk-update-status', {
        contentIds,
        status,
        
      });
    } catch (error) {
      console.error('Failed to bulk update orphan content status:', error);
      throw error;
    }
  }

  async bulkAssignClaimRequests(requestIds: string[], reviewerId: string): Promise<ClaimRequest[]> {
    try {
      const response = await apiClient.post<StrapiResponse<ClaimRequest[]>>('/api/claim-requests/bulk-assign', {
        requestIds,
        reviewerId,
        assignedAt: new Date().toISOString()
      });

      return response.data.data || [];
    } catch (error) {
      console.error('Failed to bulk assign claim requests:', error);
      throw error;
    }
  }

  // Search and discovery
  async getOrphanContentSuggestions(userId: string): Promise<OrphanContent[]> {
    const queryParams = {
      'userId': userId,
      'limit': '10'
    };

    try {
      const response = await apiClient.get<StrapiResponse<OrphanContent[]>>('/api/orphan-contents/suggestions', {
        params: queryParams
      });
      
      return response.data.data || [];
    } catch (error) {
      console.warn('Failed to get orphan content suggestions:', error);
      return [];
    }
  }

  async getSimilarOrphanContents(contentId: string, limit: number = 5): Promise<OrphanContent[]> {
    const queryParams = {
      'limit': limit.toString()
    };

    try {
      const response = await apiClient.get<StrapiResponse<OrphanContent[]>>(`/api/orphan-contents/${contentId}/similar`, {
        params: queryParams
      });
      
      return response.data.data || [];
    } catch (error) {
      console.warn('Failed to get similar orphan contents:', error);
      return [];
    }
  }

  // Notification and communication
  async notifyClaimantUpdate(requestId: string, message: string): Promise<void> {
    try {
      await apiClient.post('/api/notifications', {
        data: {
          type: 'claim_request_update',
          relatedEntity: 'claim-request',
          relatedId: requestId,
          message,
          
        }
      });
    } catch (error) {
      console.warn('Failed to notify claimant update:', error);
    }
  }

  async sendClaimRequestMessage(requestId: string, message: string, attachments?: File[]): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('requestId', requestId);
      formData.append('message', message);

      if (attachments && attachments.length > 0) {
        attachments.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file);
        });
      }

      await apiClient.upload('POST', '/api/claim-requests/messages', formData);
    } catch (error) {
      console.error('Failed to send claim request message:', error);
      throw error;
    }
  }

  // Export and reporting
  async exportOrphanContents(format: 'csv' | 'xlsx' | 'pdf' = 'csv', filters?: any): Promise<Blob> {
    const queryParams: Record<string, string> = {
      'format': format
    };

    if (filters) {
      queryParams.filters = JSON.stringify(filters);
    }

    const response = await apiClient.get('/api/orphan-contents/export', {
      params: queryParams,
      responseType: 'blob'
    });

    return response.data;
  }

  async exportClaimRequests(format: 'csv' | 'xlsx' | 'pdf' = 'csv', filters?: any): Promise<Blob> {
    const queryParams: Record<string, string> = {
      'format': format
    };

    if (filters) {
      queryParams.filters = JSON.stringify(filters);
    }

    const response = await apiClient.get('/api/claim-requests/export', {
      params: queryParams,
      responseType: 'blob'
    });

    return response.data;
  }

  // Helper methods
  private buildSortParams(sortBy?: string): string {
    switch (sortBy) {
      case 'potentialRevenue':
        return 'potentialRevenue:desc';
      case 'createdAt':
        return 'createdAt:desc';
      case 'title':
        return 'title:asc';
      default:
        return 'potentialRevenue:desc';
    }
  }

  private addFiltersToQueryParams(queryParams: Record<string, any>, filters: any): void {
    // Content type filter
    if (filters.contentType) {
      queryParams['filters[contentType][$eq]'] = filters.contentType;
    }

    // Potential revenue range filter
    if (filters.potentialRevenueRange) {
      if (filters.potentialRevenueRange.min > 0) {
        queryParams['filters[potentialRevenue][$gte]'] = filters.potentialRevenueRange.min.toString();
      }
      if (filters.potentialRevenueRange.max > 0) {
        queryParams['filters[potentialRevenue][$lte]'] = filters.potentialRevenueRange.max.toString();
      }
    }

    // Date range filter
    if (filters.dateRange) {
      if (filters.dateRange.from) {
        queryParams['filters[createdAt][$gte]'] = filters.dateRange.from;
      }
      if (filters.dateRange.to) {
        queryParams['filters[createdAt][$lte]'] = filters.dateRange.to;
      }
    }

    // Status filter
    if (filters.status && filters.status !== 'available') {
      queryParams['filters[status][$eq]'] = filters.status;
    }
  }

  // AI-powered features
  async detectPotentialMatches(orphanContentId: string): Promise<any[]> {
    try {
      const response = await apiClient.post<{ matches: any[] }>(`/api/orphan-contents/${orphanContentId}/detect-matches`);
      return response.data.matches || [];
    } catch (error) {
      console.warn('Failed to detect potential matches:', error);
      return [];
    }
  }

  async analyzeCopyrightEvidence(claimRequestId: string): Promise<any> {
    try {
      const response = await apiClient.post<{ analysis: any }>(`/api/claim-requests/${claimRequestId}/analyze-evidence`);
      return response.data.analysis || {};
    } catch (error) {
      console.warn('Failed to analyze copyright evidence:', error);
      return {};
    }
  }

  async generateOwnershipReport(orphanContentId: string): Promise<Blob> {
    const response = await apiClient.get(`/api/orphan-contents/${orphanContentId}/ownership-report`, {
      responseType: 'blob'
    });
    
    return response.data;
  }

  // Integration with external databases
  async searchExternalDatabases(query: string, databases: string[]): Promise<any[]> {
    try {
      const response = await apiClient.post<{ results: any[] }>('/api/orphan-contents/external-search', {
        query,
        databases
      });
      
      return response.data.results || [];
    } catch (error) {
      console.warn('Failed to search external databases:', error);
      return [];
    }
  }

  async verifyAuthorshipClaim(claimRequestId: string, externalSources: string[]): Promise<any> {
    try {
      const response = await apiClient.post<{ verification: any }>(`/api/claim-requests/${claimRequestId}/verify-authorship`, {
        externalSources
      });
      
      return response.data.verification || {};
    } catch (error) {
      console.warn('Failed to verify authorship claim:', error);
      return {};
    }
  }
}

export const orphanContentService = new OrphanContentService();
export default orphanContentService;